import "./Forms.scss";

import React, { useEffect, useMemo, useState } from "react";
import { Button, Spinner } from "react-bootstrap";

import { CustomModal } from "../../../../components/CustomModal";
import type { IOption } from "../../../../components/StyledSelect/StyledSelect";
import { StyledSelect as Select } from "../../../../components/StyledSelect/StyledSelect";
import { useOrganizationContext } from "../../../../contexts/organizations/organizationsContext";
import type { IApplication } from "../../../../hooks/reactQueryHooks/useApplications";
import { useExportCacheItems } from "../../../../hooks/reactQueryHooks/useCacheItems";
import { downloadCSV } from "../../../../utils/downloadCSV";

interface IProps {
  onCancel: () => void;
  applications: IApplication[];
}

const ExportCacheItemForm: React.FC<IProps> = ({ onCancel, applications }) => {
  const [inputValue, setInputValue] = useState<IOption>({
    value: "",
    label: "",
  });

  const { currentOrganization } = useOrganizationContext();
  const { refetch, isSuccess, data, isFetching, isLoading, isError } =
    useExportCacheItems(currentOrganization?.id || "", {
      ApplicationId: inputValue?.value || "",
    });

  const options = useMemo(() => {
    const options = applications.map(application => ({
      value: application.id,
      label: application.name,
    }));
    return [
      {
        value: "",
        label: "All projects",
      },
      ...options,
    ];
  }, [applications]);

  const handleSubmit = () => {
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    refetch();
  };

  useEffect(() => {
    if (isSuccess) {
      downloadCSV(data, `${inputValue?.label || ""}`);
      onCancel();
    }
  }, [isSuccess, data, inputValue?.label, onCancel]);

  const footerButtons = () => {
    return (
      <>
        <Button
          className="btn btn-secondary btn-sm"
          size="sm"
          variant="secondary"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="btn btn-primary"
          disabled={false}
          type="submit"
          onClick={handleSubmit}
        >
          Export
          {(isFetching || isLoading) && (
            <Spinner className="ms-2" size="sm" animation="border" />
          )}
        </Button>
      </>
    );
  };

  const handleSelectChange = (option: IOption) => {
    setInputValue(option || { value: "", label: "" });
  };

  return (
    <CustomModal
      show={true}
      handleClose={onCancel}
      title="Export cache items"
      footer={footerButtons()}
      errorMessage={
        isError ? "Something went wrong. Please try again later." : ""
      }
    >
      <div>
        <p className="m-0">
          Export a CSV file with cache items. Use the filter below to narrow
          down the results.
        </p>
        <div className="mt-7">
          <Select
            options={options}
            isClearable={inputValue.value !== ""}
            isDisabled={false}
            isLoading={false}
            isSearchable={false}
            onChange={handleSelectChange}
            placeholder={options[0]?.label || ""}
            aria-label="Select collection"
            isBootstrap={false}
          />
        </div>
        <label className="short-explanation">Filter by project</label>
      </div>
    </CustomModal>
  );
};

export { ExportCacheItemForm };
